import React, { useCallback, useEffect, useState } from 'react';

import { RadioChangeEvent } from 'antd';
import styled from 'styled-components';

import { saveCommunitiesEmailNotificationRecipients } from 'apis/CommunityAPI';
import { useRootContext } from 'components/layout/RootContext';
import Radio from 'components/lib/Radio';
import SettingsPage from 'components/Settings/SettingsPage';
import { CommunityPreferenceNotification, CommunityPreferenceType } from 'types/communities';

import MarketRateComparisonPreferences from './MarketRateComparisonPreferences';
import MarketRateComparisonStatus from './MarketRateComparisonStatus';
import {
  COMMUNITY_SETTINGS_PAGE_TYPE,
  DEFAULT_MARKET_RATE_PERCENT,
  EMAIL_NOTIFICATION_TYPE,
  MARKET_RATE_COMPARISON_UPDATES,
} from '../../constants';
import { MarketRateComparisonUpdateSettings } from '../../types';
import EmailNotificationsRecipientsModal from '../EmailNotificationsRecipientsModal';

const getItemStatusComponent = (item: CommunityPreferenceType) => {
  return <MarketRateComparisonStatus item={item} />;
};

const Settings = styled.div`
  padding-top: 10px;
  padding-right: 20px;
  min-height: 350px;
  max-width: 442px;
`;

const MarketRateComparisonUpdates = () => {
  const { showMessage } = useRootContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [communitiesToModify, setCommunitiesToModify] = useState<CommunityPreferenceType[]>([]);
  const [refetchCommunities, setRefetchCommunities] = useState<() => void | undefined>();
  const [clearSelection, setClearSelection] = useState<() => void | undefined>();
  const [marketRateComparisonSettings, setMarketRateComparisonSettings] = useState<
    MarketRateComparisonUpdateSettings | undefined
  >(undefined);

  const showCommunitiesEditModal = (items: any[]) => {
    setCommunitiesToModify(items);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (!isModalOpen) {
      setMarketRateComparisonSettings(undefined);
      return;
    }

    const preferences = communitiesToModify[0]?.preferences;
    const overRate = preferences?.market_rate_comparison_rate_over;
    const underRate = preferences?.market_rate_comparison_rate_under;
    setMarketRateComparisonSettings({
      enabled: true,
      overMarketRate: {
        enabled: overRate !== null && overRate !== undefined,
        value: overRate !== null && overRate !== undefined ? Number(overRate) : DEFAULT_MARKET_RATE_PERCENT,
      },
      underMarketRate: {
        enabled: underRate !== null && underRate !== undefined,
        value: underRate !== null && underRate !== undefined ? Number(underRate) : DEFAULT_MARKET_RATE_PERCENT,
      },
    });
  }, [isModalOpen, communitiesToModify]);

  const handleMarketRateSettingsChange = useCallback((settings: MarketRateComparisonUpdateSettings) => {
    setMarketRateComparisonSettings(settings);
  }, []);

  const savePreferenceRecipients = (newStatus: number, preferenceNotifications: CommunityPreferenceNotification[]) => {
    setLoading(true);

    const payload: any = {
      communities: preferenceNotifications,
      email_notifications_enabled: newStatus,
    };
    payload.market_rate_comparison_rate_over =
      marketRateComparisonSettings?.enabled && marketRateComparisonSettings.overMarketRate.enabled
        ? marketRateComparisonSettings.overMarketRate.value
        : null;
    payload.market_rate_comparison_rate_under =
      marketRateComparisonSettings?.enabled && marketRateComparisonSettings.underMarketRate.enabled
        ? marketRateComparisonSettings.underMarketRate.value
        : null;

    saveCommunitiesEmailNotificationRecipients(EMAIL_NOTIFICATION_TYPE.MARKET_RATE_COMPARISON_UPDATES, payload)
      .then(() => {
        showMessage('success', 'All selected communities updated successfully');
        if (refetchCommunities) refetchCommunities();
        if (clearSelection) clearSelection();
      })
      .catch(() => {
        showMessage('error', 'Failed to update one or more communities!');
      })
      .finally(() => {
        setLoading(false);
        setIsModalOpen(false);
      });
  };

  const renderSettingsSection = useCallback(
    ({ value, setStatusValue, currentSetting, modalDescription }: any) => (
      <Settings>
        <span style={{ fontSize: '14px' }}>{modalDescription}</span>
        <Radio.Group
          style={{
            display: 'flex',
            flexDirection: 'row',
            marginTop: 15,
          }}
          value={value}
          onChange={(e: RadioChangeEvent) => {
            setStatusValue(e.target.value);
          }}
        >
          {Object.values(currentSetting.statusElements)
            .sort((a: any, b: any) => Number(a.value) - Number(b.value))
            .map((status: any) => (
              <Radio className={'large'} key={status.value} value={status.value}>
                {status.label}
              </Radio>
            ))}
        </Radio.Group>
        <MarketRateComparisonPreferences
          notificationEnabled={value === currentSetting.statusElements.ON.value}
          initialOverMarketRateEnabled={marketRateComparisonSettings?.overMarketRate?.enabled}
          initialUnderMarketRateEnabled={marketRateComparisonSettings?.underMarketRate?.enabled}
          initialOverMarketRate={marketRateComparisonSettings?.overMarketRate?.value}
          initialUnderMarketRate={marketRateComparisonSettings?.underMarketRate?.value}
          onChange={handleMarketRateSettingsChange}
        />
      </Settings>
    ),
    [marketRateComparisonSettings, handleMarketRateSettingsChange]
  );

  return (
    <>
      <SettingsPage
        filterOptions={Object.values(MARKET_RATE_COMPARISON_UPDATES.statusFilterElements)}
        getItemStatusComponent={getItemStatusComponent}
        showCommunitiesEditModal={showCommunitiesEditModal}
        settingsPageType={COMMUNITY_SETTINGS_PAGE_TYPE.MARKET_RATE_COMPARISON}
        setRefetchCommunities={setRefetchCommunities}
        setClearSelection={setClearSelection}
        filterField={MARKET_RATE_COMPARISON_UPDATES.field}
        notificationType={EMAIL_NOTIFICATION_TYPE.MARKET_RATE_COMPARISON_UPDATES}
      />
      <EmailNotificationsRecipientsModal
        onClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        loading={loading}
        title={MARKET_RATE_COMPARISON_UPDATES.title}
        communitiesToModify={communitiesToModify}
        onSave={savePreferenceRecipients}
        settingsPageType={COMMUNITY_SETTINGS_PAGE_TYPE.MARKET_RATE_COMPARISON}
        renderSettingsSection={renderSettingsSection}
      />
    </>
  );
};

export default MarketRateComparisonUpdates;
